'use client';

import clsx from 'clsx';
import Link from 'next/link';
import {
    memo,
    type ComponentPropsWithoutRef,
    type ComponentType,
    type MouseEvent,
    type PointerEvent,
    type ReactElement,
    type ReactNode,
    useCallback,
    useRef,
    useState,
} from 'react';

import { pushAppToast } from '@/lib/ui/globalAppToast';
import MuiButton from '@mui/material/Button';

import styles from './Button.module.scss';

const MuiButtonLoose = MuiButton as ComponentType<Record<string, unknown>>;
const disableInteractiveRipple = process.env.NODE_ENV === 'test';

export type UiButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost';
export type UiButtonSize = 'default' | 'compact';

type ButtonCopyProps =
    | {
          copyFailedMessage: string;
          copyLabel: string;
          copySuccessMessage: string;
          copyTestId?: string;
          copyText: string;
      }
    | {
          copyFailedMessage?: never;
          copyLabel?: never;
          copySuccessMessage?: never;
          copyTestId?: never;
          copyText?: never;
      };

type SharedProps = ButtonCopyProps & {
    children: ReactNode;
    className?: string;
    navigationMode?: 'client' | 'document';
    variant?: UiButtonVariant;
    size?: UiButtonSize;
};

type ButtonAsButtonProps = SharedProps &
    Omit<ComponentPropsWithoutRef<'button'>, 'children'> & {
        href?: never;
    };

type ButtonAsLinkProps = SharedProps &
    Omit<ComponentPropsWithoutRef<typeof Link>, 'children'> & {
        href: string;
    };

export type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

const resolveMuiVariant = (variant: UiButtonVariant) => {
    if (variant === 'secondary') {
        return 'outlined';
    }

    if (variant === 'ghost' || variant === 'tertiary') {
        return 'text';
    }

    return 'contained';
};

const ButtonComponent = (props: ButtonProps) => {
    const {
        children,
        className,
        copyFailedMessage,
        copyLabel,
        copySuccessMessage,
        copyTestId,
        copyText,
        navigationMode = 'client',
        size = 'default',
        variant = 'primary',
    } = props;
    const [isCopyRevealed, setIsCopyRevealed] = useState(false);
    const copyRevealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const copyLongPressActivatedRef = useRef(false);
    const buttonClassName = clsx(styles.button, styles[variant], size === 'compact' && styles.compact, className);
    const hasCopyAffordance = Boolean(copyText);
    const muiVariant = resolveMuiVariant(variant);
    const muiSize = size === 'compact' ? 'small' : 'medium';
    const clearCopyRevealTimer = useCallback(() => {
        if (copyRevealTimerRef.current) {
            clearTimeout(copyRevealTimerRef.current);
            copyRevealTimerRef.current = null;
        }
    }, []);
    const revealCopyForTouch = useCallback(
        (event: PointerEvent<HTMLElement>) => {
            if (!hasCopyAffordance || event.pointerType === 'mouse') {
                return;
            }

            clearCopyRevealTimer();
            copyRevealTimerRef.current = setTimeout(() => {
                copyLongPressActivatedRef.current = true;
                setIsCopyRevealed(true);
            }, 420);
        },
        [clearCopyRevealTimer, hasCopyAffordance]
    );
    const cancelCopyRevealForTouch = useCallback(() => {
        clearCopyRevealTimer();
    }, [clearCopyRevealTimer]);
    const preventLongPressNavigation = useCallback((event: MouseEvent<HTMLElement>) => {
        if (!copyLongPressActivatedRef.current) {
            return;
        }

        const target = event.target;

        if (target instanceof Element && target.closest(`.${styles.copyAffordance}`)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        copyLongPressActivatedRef.current = false;
    }, []);
    const copyButtonText = useCallback(async () => {
        if (!copyText || !copyLabel || !copySuccessMessage || !copyFailedMessage) {
            return;
        }

        try {
            await navigator.clipboard.writeText(copyText);
            copyLongPressActivatedRef.current = false;
            setIsCopyRevealed(false);
            pushAppToast({
                message: copySuccessMessage,
                severity: 'success',
            });
        } catch {
            copyLongPressActivatedRef.current = false;
            pushAppToast({
                message: copyFailedMessage,
                severity: 'error',
            });
        }
    }, [copyFailedMessage, copyLabel, copySuccessMessage, copyText]);
    const renderWithCopyAffordance = useCallback(
        (buttonElement: ReactElement) => {
            if (!copyText || !copyLabel || !copySuccessMessage || !copyFailedMessage) {
                return buttonElement;
            }

            return (
                <span
                    className={styles.copyShell}
                    data-copy-revealed={isCopyRevealed ? 'true' : undefined}
                    onClickCapture={preventLongPressNavigation}
                    onPointerCancel={cancelCopyRevealForTouch}
                    onPointerDown={revealCopyForTouch}
                    onPointerLeave={cancelCopyRevealForTouch}
                    onPointerUp={cancelCopyRevealForTouch}
                >
                    {buttonElement}
                    <button
                        aria-label={copyLabel}
                        className={styles.copyAffordance}
                        data-testid={copyTestId}
                        onClick={copyButtonText}
                        title={copyLabel}
                        type="button"
                    >
                        <svg aria-hidden="true" className={styles.copyIcon} focusable="false" viewBox="0 0 24 24">
                            <path d="M8 7.5A2.5 2.5 0 0 1 10.5 5h6A2.5 2.5 0 0 1 19 7.5v6A2.5 2.5 0 0 1 16.5 16h-6A2.5 2.5 0 0 1 8 13.5z" />
                            <path d="M5 10.5A2.5 2.5 0 0 1 7.5 8H8v5.5a2.5 2.5 0 0 0 2.5 2.5H16v.5a2.5 2.5 0 0 1-2.5 2.5h-6A2.5 2.5 0 0 1 5 16.5z" />
                        </svg>
                    </button>
                </span>
            );
        },
        [
            cancelCopyRevealForTouch,
            copyButtonText,
            copyFailedMessage,
            copyLabel,
            copySuccessMessage,
            copyTestId,
            copyText,
            isCopyRevealed,
            preventLongPressNavigation,
            revealCopyForTouch,
        ]
    );

    if ('href' in props && props.href) {
        const {
            href,
            children: _children,
            copyFailedMessage: _copyFailedMessage,
            copyLabel: _copyLabel,
            copySuccessMessage: _copySuccessMessage,
            copyTestId: _copyTestId,
            copyText: _copyText,
            variant: _variant,
            navigationMode: _navigationMode,
            prefetch,
            replace,
            scroll,
            size: _size,
            className: _className,
            ...rest
        } = props;
        void _children;
        void _copyFailedMessage;
        void _copyLabel;
        void _copySuccessMessage;
        void _copyTestId;
        void _copyText;
        void _variant;
        void _navigationMode;
        void _size;
        void _className;

        if (navigationMode === 'document') {
            return renderWithCopyAffordance(
                <MuiButtonLoose
                    className={buttonClassName}
                    component="a"
                    data-size={size}
                    data-variant={variant}
                    disableRipple={disableInteractiveRipple}
                    size={muiSize}
                    variant={muiVariant}
                    href={href}
                    {...rest}
                    data-navigation-mode="document"
                >
                    {children}
                </MuiButtonLoose>
            );
        }

        return renderWithCopyAffordance(
            <MuiButtonLoose
                className={buttonClassName}
                component={Link}
                data-size={size}
                data-variant={variant}
                disableRipple={disableInteractiveRipple}
                href={href}
                prefetch={prefetch}
                replace={replace}
                scroll={scroll}
                size={muiSize}
                variant={muiVariant}
                {...rest}
            >
                {children}
            </MuiButtonLoose>
        );
    }

    const {
        type = 'button',
        children: _children,
        copyFailedMessage: _copyFailedMessage,
        copyLabel: _copyLabel,
        copySuccessMessage: _copySuccessMessage,
        copyTestId: _copyTestId,
        copyText: _copyText,
        variant: _variant,
        navigationMode: _navigationMode,
        size: _size,
        className: _className,
        ...rest
    } = props;
    void _children;
    void _copyFailedMessage;
    void _copyLabel;
    void _copySuccessMessage;
    void _copyTestId;
    void _copyText;
    void _variant;
    void _navigationMode;
    void _size;
    void _className;

    return renderWithCopyAffordance(
        <MuiButtonLoose
            className={buttonClassName}
            data-size={size}
            data-variant={variant}
            disableRipple={disableInteractiveRipple}
            size={muiSize}
            type={type as 'button' | 'submit' | 'reset'}
            variant={muiVariant}
            {...rest}
        >
            {children}
        </MuiButtonLoose>
    );
};

ButtonComponent.displayName = 'Button';

export const Button = memo(ButtonComponent) as typeof ButtonComponent;
